import { useMemo, useState } from 'react';
import {
  Loader2,
  AlertCircle,
  Star,
  Trophy,
  ArrowUp,
  ArrowDown,
  Plus,
  X,
  Search,
  MapPin,
} from 'lucide-react';
import { PropertyReviewStatus } from '@pms/shared';
import type { FeaturedAdminItem } from '@pms/shared';
import {
  useFeatured,
  useSetFeatured,
  useUnfeature,
  useReorderFeatured,
  useEditorsPick,
} from '@/hooks/api/usePlatform';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { ReviewStatusBadge } from '@/components/ui/ReviewStatusBadge';
import { extractErrorMessage } from '@/lib/error';
import { cn } from '@/lib/utils';

// ── Featured set row (left column) ───────────────────────────────────────────

function FeaturedRow({
  item,
  index,
  count,
  onMoveUp,
  onMoveDown,
  onRemove,
  onPick,
  busy,
}: {
  item: FeaturedAdminItem;
  index: number;
  count: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  onPick: () => void;
  busy: boolean;
}) {
  const regressed = item.reviewStatus !== PropertyReviewStatus.APPROVED;
  const suppressed = regressed || item.hidden || !item.isActive;

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      {/* Reorder controls */}
      <div className="flex flex-col">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={index === 0 || busy}
          aria-label={`Move ${item.name} up`}
          className="p-0.5 rounded text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <ArrowUp size={16} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={index === count - 1 || busy}
          aria-label={`Move ${item.name} down`}
          className="p-0.5 rounded text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <ArrowDown size={16} aria-hidden="true" />
        </button>
      </div>

      {/* Editor's Pick toggle */}
      <button
        type="button"
        onClick={onPick}
        disabled={busy || suppressed}
        role="radio"
        aria-checked={item.editorsPick}
        aria-label={`Set ${item.name} as Editor's Pick`}
        title={
          suppressed
            ? 'Only an approved, visible property can be the Editor’s Pick'
            : "Editor's Pick"
        }
        className={cn(
          'shrink-0 rounded-full p-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-40 disabled:cursor-not-allowed',
          item.editorsPick
            ? 'text-amber-500'
            : 'text-gray-300 hover:text-amber-400',
        )}
      >
        <Star size={18} fill={item.editorsPick ? 'currentColor' : 'none'} aria-hidden="true" />
      </button>

      {/* Details */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-gray-900">{item.name}</p>
          {item.editorsPick && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
              <Trophy size={11} aria-hidden="true" /> Editor's Pick
            </span>
          )}
        </div>
        <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
          <MapPin size={12} aria-hidden="true" />
          {[item.city, item.country].filter(Boolean).join(', ') || '—'}
          <span className="text-gray-300">·</span>
          {item.operatorName}
        </p>
      </div>

      {/* Status / warning */}
      <div className="flex shrink-0 items-center gap-2">
        {suppressed ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
            <AlertCircle size={12} aria-hidden="true" />
            {regressed ? 'Not approved' : item.hidden ? 'Hidden' : 'Inactive'}
          </span>
        ) : (
          <ReviewStatusBadge status={item.reviewStatus} />
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          disabled={busy}
          aria-label={`Remove ${item.name} from featured`}
        >
          <X size={15} aria-hidden="true" /> Remove
        </Button>
      </div>
    </li>
  );
}

// ── Eligible pool row (right column) ─────────────────────────────────────────

function PoolRow({
  item,
  onFeature,
  busy,
}: {
  item: FeaturedAdminItem;
  onFeature: () => void;
  busy: boolean;
}) {
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900">{item.name}</p>
        <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
          <MapPin size={12} aria-hidden="true" />
          {[item.city, item.country].filter(Boolean).join(', ') || '—'}
          <span className="text-gray-300">·</span>
          {item.operatorName}
        </p>
      </div>
      <Button variant="secondary" size="sm" onClick={onFeature} disabled={busy}>
        <Plus size={15} aria-hidden="true" /> Feature
      </Button>
    </li>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AdminFeaturedPage() {
  const { data, isLoading, isError, refetch } = useFeatured();
  const setFeatured = useSetFeatured();
  const unfeature = useUnfeature();
  const reorder = useReorderFeatured();
  const editorsPick = useEditorsPick();

  const [poolQuery, setPoolQuery] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const featured = data?.featured ?? [];
  const eligible = data?.eligible ?? [];

  const busy =
    setFeatured.isPending ||
    unfeature.isPending ||
    reorder.isPending ||
    editorsPick.isPending;

  const filteredPool = useMemo(() => {
    const q = poolQuery.trim().toLowerCase();
    if (!q) return eligible;
    return eligible.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.city.toLowerCase().includes(q) ||
        p.country.toLowerCase().includes(q),
    );
  }, [eligible, poolQuery]);

  function run<T>(mutate: () => Promise<T>) {
    setActionError(null);
    void mutate().catch((err) => setActionError(extractErrorMessage(err)));
  }

  function move(index: number, dir: -1 | 1) {
    const next = [...featured];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    const a = next[index];
    const b = next[target];
    if (!a || !b) return;
    next[index] = b;
    next[target] = a;
    run(() => reorder.mutateAsync(next.map((p) => p.id)));
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Featured Hotels"
        description="Curate the storefront homepage: pick approved hotels, set their order, and choose one Editor's Pick."
      />

      <p className="text-sm text-gray-500">
        The Editor's Pick appears as the large banner on the homepage; the rest render
        as the grid below it, in the order shown here.
      </p>

      {actionError && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {actionError}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white py-16 shadow-sm">
          <Loader2 size={24} className="animate-spin text-brand-600" aria-hidden="true" />
          <span className="ml-2 text-sm text-gray-500">Loading featured hotels…</span>
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-gray-200 bg-white py-16 text-center shadow-sm">
          <AlertCircle size={24} className="mx-auto mb-3 text-red-500" aria-hidden="true" />
          <p className="mb-4 text-sm text-red-700">Unable to load featured hotels.</p>
          <Button variant="secondary" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Featured set */}
          <section
            aria-labelledby="featured-set-h"
            className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
          >
            <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <h2 id="featured-set-h" className="text-sm font-semibold text-gray-900">
                Featured set
              </h2>
              <span className="text-xs text-gray-400">{featured.length} hotels</span>
            </header>
            {featured.length === 0 ? (
              <EmptyState
                icon={Star}
                title="No featured hotels yet"
                description="Add approved properties from the eligible list on the right."
              />
            ) : (
              <ul
                className="divide-y divide-gray-100"
                role="radiogroup"
                aria-label="Featured hotels (Editor's Pick selection)"
              >
                {featured.map((item, i) => (
                  <FeaturedRow
                    key={item.id}
                    item={item}
                    index={i}
                    count={featured.length}
                    busy={busy}
                    onMoveUp={() => move(i, -1)}
                    onMoveDown={() => move(i, 1)}
                    onRemove={() => run(() => unfeature.mutateAsync(item.id))}
                    onPick={() => run(() => editorsPick.mutateAsync(item.id))}
                  />
                ))}
              </ul>
            )}
          </section>

          {/* Eligible pool */}
          <section
            aria-labelledby="eligible-pool-h"
            className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
          >
            <header className="border-b border-gray-100 px-4 py-3">
              <div className="flex items-center justify-between">
                <h2 id="eligible-pool-h" className="text-sm font-semibold text-gray-900">
                  Eligible pool
                </h2>
                <span className="text-xs text-gray-400">{eligible.length} approved</span>
              </div>
              {eligible.length > 0 && (
                <div className="relative mt-3">
                  <Search
                    size={15}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    aria-hidden="true"
                  />
                  <input
                    type="search"
                    value={poolQuery}
                    onChange={(e) => setPoolQuery(e.target.value)}
                    placeholder="Search approved properties…"
                    aria-label="Search eligible properties"
                    className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              )}
            </header>
            {eligible.length === 0 ? (
              <EmptyState
                icon={Star}
                title="No approved properties to feature yet"
                description="Approve properties in Listing Review to make them eligible."
              />
            ) : filteredPool.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-gray-400">
                No properties match “{poolQuery}”.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {filteredPool.map((item) => (
                  <PoolRow
                    key={item.id}
                    item={item}
                    busy={busy}
                    onFeature={() => run(() => setFeatured.mutateAsync(item.id))}
                  />
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
